import type { TSupportedLocale } from '@docue/builder/i18n'
import type { TJurisdictionCode } from '@docue/builder-intl'

import { queryKeys } from './useQueryUserTemplateV2'
import { useQueryClientUtils } from '../../useQueryClientUtils'
import { useFetchUserTemplateV2 } from './useFetchUserTemplateV2'
import { queryKeys as queryKeysV1 } from '../user-template/useQueryUserTemplate'
import { queryKeys as queryKeysFavourite } from '../favourite/useQueryFavourite'
import { queryKeys as queryKeysRecent } from '../recent-template/useQueryRecentTemplate'

const getQueryKeys = ({
  id,
  workspaceId,
}: {
  id: string
  workspaceId: string
}) => {
  const { language } = useLanguage()

  return {
    v1ById: queryKeysV1.byId({ id, fieldsToInclude: ['template'] }).queryKey,
    v1All: queryKeysV1.all({ workspaceId }).queryKey,
    v2ById: queryKeys.byId({ id }).queryKey,
    favourites: queryKeysFavourite.all({ workspaceId }).queryKey,
    recent: queryKeysRecent.all({ locale: language, workspaceId }).queryKey,
  }
}

const create = () => {
  const { cancelQueries, invalidateQueries } = useQueryClientUtils()
  const { create } = useFetchUserTemplateV2()

  return useMutation({
    mutationFn: ({
      name,
      language,
      jurisdiction,
    }: {
      name: string
      language: TSupportedLocale
      jurisdiction: TJurisdictionCode
      workspaceId: string
    }) => create({ name, language, jurisdiction }),
    onMutate: ({ workspaceId }) => cancelQueries([queryKeysV1.all({ workspaceId }).queryKey]),
    onSettled: (_, _1, { workspaceId }) => invalidateQueries([queryKeysV1.all({ workspaceId }).queryKey]),
  })
}

const update = () => {
  const { cancelQueries, invalidateQueries } = useQueryClientUtils()
  const { update } = useFetchUserTemplateV2()

  return useMutation({
    mutationFn: update,
    onMutate: ({ id, workspaceId }) => cancelQueries(Object.values(getQueryKeys({ id, workspaceId }))),
    onSuccess: (_responseData, { workspaceId }) => {
      invalidateQueries([queryKeysFavourite.all({ workspaceId }).queryKey])
    },
    onError: (_, { workspaceId, id }) => invalidateQueries(Object.values(getQueryKeys({ workspaceId, id }))),
  })
}

export const useMutationUserTemplateV2 = {
  create,
  update,
}
